import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { useEdgesState } from 'reactflow';
import { setEdgesActionCreator } from '../redux/reducers/global/edge/edgeSlice';

export const useEdgeOperations = (initialEdges = []) => {
    const dispatch = useDispatch();
    const currentEdges = useSelector((state) => state.edge.edges);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const skipSyncRef = useRef(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Helper: remove transient fields for comparisons
    const sanitize = useCallback((arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.map(({ selected, ...rest }) => rest);
    }, []);

    const equalByContent = useCallback((a, b) => {
        try {
            const sa = JSON.stringify(sanitize(a));
            const sb = JSON.stringify(sanitize(b));
            return sa === sb;
        } catch {
            return false;
        }
    }, [sanitize]);

    // Synchronize local state with Redux state
    useEffect(() => {
        // Preserve current selection state when syncing Redux -> local
        // syncing edges from Redux to local
        setEdges((prev) => {
            if (!Array.isArray(currentEdges)) return prev;
            // If content identical, skip setting to avoid loops
            if (equalByContent(prev, currentEdges)) return prev;
            const byId = new Map((prev || []).map(e => [e.id, e]));
            return currentEdges.map((e) => {
                const prevEdge = byId.get(e.id);
                return prevEdge ? { ...e, selected: prevEdge.selected } : e;
            });
        });
        if (!isInitialized) {
            setIsInitialized(true);
        }
    }, [currentEdges, equalByContent, isInitialized, setEdges]);

    // Debounce update function to batch and limit updates to Redux
    // Do not propagate selection-only changes to Redux, and strip transient fields
    const updateReduxEdges = useMemo(() => debounce((nextEdges, latestCurrentEdges) => {
        if (skipSyncRef.current) {
            skipSyncRef.current = false;
            return;
        }
        // Only update Redux if content actually differs
        if (!equalByContent(nextEdges, latestCurrentEdges)) {
            const sanitized = sanitize(nextEdges);
            // sync local->Redux
            dispatch(setEdgesActionCreator({ edges: sanitized }));
        }
    }, 30), [dispatch, equalByContent, sanitize]);

    // Synchronize Redux state with local state
    useEffect(() => {
        if ((edges.length && !isInitialized) || isInitialized) {
            updateReduxEdges(edges, currentEdges);
        }
    }, [edges, currentEdges, isInitialized, updateReduxEdges]);

    useEffect(() => {
        return () => updateReduxEdges.cancel();
    }, [updateReduxEdges]);

    // React Flow internal function to handle changes like positioning or redimensioning
    const handleEdgesChange = useCallback((changes) => {
        // handleEdgesChange
        // If only selection changes, avoid syncing to Redux to prevent loops
        if (Array.isArray(changes) && changes.length > 0) {
            const nonSelectChanges = changes.filter((c) => c.type !== 'select');
            if (nonSelectChanges.length === 0) {
                skipSyncRef.current = true;
                // selection-only change; skip Redux sync
            }
        }
        onEdgesChange(changes);
    }, [onEdgesChange]);

    const clearLocalEdgeSelection = useCallback(() => {
        // clear local edge selection
        // prevent syncing this selection-only change back to Redux
        skipSyncRef.current = true;
        setEdges((eds) => (Array.isArray(eds) ? eds.map((e) => (e?.selected ? { ...e, selected: false } : e)) : eds));
    }, [setEdges]);

    return { setEdges, handleEdgesChange, clearLocalEdgeSelection };
};
